// @flow

import type { Router } from 'app/common/lib/ReactRouter/types';
import type { State as Pin } from 'app/common/redux/reducers/pin';
import type { State as Pins } from 'app/common/redux/reducers/pins';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import { appendQuery } from 'app/packages/url';
import { countryIsEligibleForQuickPromote } from 'app/packages/business/businessUtils';
import { type RequestContextType } from 'app/common/react/components/RequestContext';
import { isResurrectedUser, isNewUser } from 'app/common/lib/ActivationUtil';
import { timeframesFromNow, MS_MINUTE } from 'app/common/lib/react/dateUtils';

/* Used to check if a user has any created pins in the redux cache
This does not gaurantee a user has never created a pin. This will be primarily
used for users who don't have any pins and create one in this session */
export const viewingUserCreatedPins = (viewer: Viewer, pins: Pins): Array<Pin> => {
  const pinIds = Object.keys(pins).filter((pinId) => {
    const pin = pins[String(pinId)];
    const pinnerId = (pin.pinner || {}).id;
    return pinnerId === viewer.id && !pin.is_repin;
  });

  return pinIds.map((id) => pins[id]);
};

/* Used to check if user has recently created a pin using FirstPinCreate */
export const viewingUserRecentlyCreatedPins = (viewer: Viewer, pins: Pins): Array<Pin> => {
  const pinsCreated = viewingUserCreatedPins(viewer, pins);
  return pinsCreated.filter((pin) => {
    if (pin.created_at) {
      const timeframes = timeframesFromNow(pin.created_at);
      return timeframes.ms < MS_MINUTE * 20;
    }
    return false;
  });
};

/* Checks Pins to see if they are quick promotable by user */
export const viewingUserQuickPromotablePins = (viewer: Viewer, pins: Pins): Array<Pin> => {
  const pinIds = Object.keys(pins).filter((pinId) => {
    const pin = pins[String(pinId)];
    const pinnerId = (pin.pinner || {}).id;
    return pinnerId === viewer.id && pin.is_quick_promotable;
  });
  return pinIds.map((id) => pins[id]);
};

export const advertiserIntent = Object.freeze({
  NOT_ANSWERED: 0,
  YES: 1,
  NO: 2,
  NOT_SURE: 3,
});

const TRANSLATED_COTERIE_COUNTRIES = [
  'en',
  'fr',
  'de',
  'es',
  'it',
  'pt',
  'nl',
  'nb',
  'fi',
  'sv',
  'da',
  'nl',
  'pt',
];

export const localeHasTranslatedCoteries = (locale: string): boolean => {
  const lowerCaseLocale = locale.toLowerCase();

  return !!TRANSLATED_COTERIE_COUNTRIES.find((translatedLocale) =>
    lowerCaseLocale.startsWith(translatedLocale),
  );
};

export const userInQuickPromoteCountry = (viewer: Viewer, requestContext: RequestContextType) => {
  if (!viewer || !viewer.isAuth) {
    return false;
  }

  return countryIsEligibleForQuickPromote(viewer.country);
};

export const canPinnerPromote = (viewer: Viewer) =>
  viewer.isAuth &&
  !viewer.isPartner &&
  !isResurrectedUser({ user: viewer }) &&
  !isNewUser({ user: viewer });

export const isUserFACEligible = (viewer: Viewer, requestContext: RequestContextType) =>
  viewer.partner &&
  viewer.partner.advertising_intent === advertiserIntent.YES &&
  userInQuickPromoteCountry(viewer, requestContext);

export const partnerDaysOld = (viewer: Viewer) =>
  viewer.partner && viewer.partner.created_at
    ? timeframesFromNow(viewer.partner.created_at).day
    : -1;

export const isCustomerSupportEligible = (viewer: Viewer) =>
  viewer.isPartner &&
  viewer.partner &&
  (viewer.partner.advertising_intent === advertiserIntent.YES ||
    viewer.partner.advertising_intent === advertiserIntent.NOT_SURE) &&
  partnerDaysOld(viewer) < 90;

type PinnerRedirectArgs = {
  requestContext: RequestContextType,
  router: Router,
  pinId: string,
};

const firstAdCreatePath = (pinId?: string) =>
  pinId ? `/business/ad_create?pinId=${pinId}` : '/business/ad_create';
const partnerConvertRedirectWithNext = (router: Router, next: string) => {
  const path = appendQuery('/business/convert', {
    next,
  });
  router.push(path);
};

export const pinnerRedirectToPromote = ({ requestContext, router, pinId }: PinnerRedirectArgs) => {
  partnerConvertRedirectWithNext(router, firstAdCreatePath(pinId));
};

export default null;
